/**
 * ExaminerReportTab — the "Examiner Report" tab of the Accountability Registry App Pack.
 *
 * Renders the examiner-ready ownership report generated from LIVE registry state (no snapshots):
 * inventory, how materiality was determined, segregation of duties (owner independent of creator),
 * attestation posture, and every open exception with its remediation. The exception list is the
 * SAME live findings operators remediate in Outcomes & Actions — one source of truth.
 *
 * Data: api.getExaminerOwnershipReport (GET /ownership/examiner-report). Export writes the report
 * JSON to the examiner's machine (client-side, no upload).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../../services/api';
import ScopeNote from './ScopeNote';
import {
  Card, Table, Pill, MetricCard, Button, EmptyState, Skeleton,
} from '../../components/v2';
import { TierBadge, Callout } from './AccountabilityUI';
import {
  FileText, ShieldCheck, UserCheck, AlertTriangle, HelpCircle, Download, RefreshCw, Scale,
} from 'lucide-react';

// apiFetch wraps success as { ok, data, etag }; unwrap to the body (tolerate a raw body).
const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

function severityTone(sev) {
  switch (sev) {
    case 'high': return 'danger';
    case 'medium': return 'warning';
    default: return 'muted';
  }
}
function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return '—'; }
}

function Section({ icon, title, hint, children }) {
  return (
    <section className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-v2-trust">{icon}</span>
        <h3 className="text-[13.5px] font-semibold text-v2-ink">{title}</h3>
      </div>
      {hint ? <p className="text-[12px] text-v2-ink-muted mb-2 -mt-1">{hint}</p> : null}
      {children}
    </section>
  );
}

export default function ExaminerReportTab({ active = true, customerId = null }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = unwrap(await api.getExaminerOwnershipReport(customerId));
      setReport(body || null);
    } catch (e) {
      setError(e?.message || 'Could not generate the examiner report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // customerId is a real dependency: without it the memoised loader keeps the customer that
  // was current when it was created, and switching customers would silently show stale scope.
  useEffect(() => { load(); }, [load]);

  // Background refresh on re-activation (keep-alive keeps this tab mounted).
  const reactivatedRef = React.useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!reactivatedRef.current) { reactivatedRef.current = true; return; }
    load();
  }, [active]);

  const exportReport = useCallback(() => {
    if (!report) return;
    try {
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `examiner-ownership-report-${(report.generatedAt || 'now').toString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { /* best-effort export */ }
  }, [report]);

  const stateRows = useMemo(() => {
    const byState = report?.inventory?.byState || {};
    return Object.entries(byState)
      .filter(([, n]) => n > 0)
      .map(([state, count]) => ({ state, count }));
  }, [report]);

  const exceptionColumns = useMemo(() => ([
    {
      key: 'finding', header: 'Exception',
      render: (f) => (
        <div>
          <div className="font-medium text-v2-ink">{f.title}</div>
          <div className="text-[12px] text-v2-ink-muted mt-0.5">{f.detail}</div>
        </div>
      ),
    },
    {
      key: 'scope', header: 'Scope',
      render: (f) => (f.agentId
        ? <span className="text-v2-ink">{f.agentName || 'Unnamed agent'}</span>
        : <span className="text-v2-ink-muted">{`${f.count} agent${f.count === 1 ? '' : 's'}`}</span>),
    },
    {
      key: 'materiality', header: 'Materiality',
      render: (f) => (f.tier
        ? <TierBadge tier={f.tier} />
        : <span className="text-v2-ink-muted">—</span>),
    },
    { key: 'severity', header: 'Severity', render: (f) => <Pill tone={severityTone(f.severity)} size="sm" dot>{f.severity}</Pill> },
    { key: 'action', header: 'Remediation', align: 'right', render: (f) => <span className="text-v2-ink-muted">{f.action}</span> },
  ]), []);

  if (loading && !report) return <div className="p-2"><Skeleton variant="text" lines={12} /></div>;
  if (error) {
    return (
      <EmptyState
        icon={<HelpCircle size={22} />}
        title="Could not generate examiner report"
        description={error}
        primaryAction={<Button variant="secondary" size="sm" onClick={load}>Try again</Button>}
      />
    );
  }
  if (!report) return null;

  const inv = report.inventory || {};
  const mat = report.materiality || {};
  const ind = report.independence || {};
  const att = report.attestation || {};
  const exc = report.exceptions || {};
  const findings = Array.isArray(exc.findings) ? exc.findings : [];

  return (
    <div>
      {/* Report header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[12px] text-v2-ink-muted">Generated from live registry state</div>
          {/* On an examiner-facing artifact the population is not a footnote — a figure nobody can
              attribute to a scope is a figure nobody can check. */}
          <ScopeNote scope={report?.scope} />
          <div className="text-[13px] text-v2-ink">{formatWhen(report.generatedAt)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={14} />} onClick={load}>Regenerate</Button>
          <Button variant="primary" size="sm" iconLeft={<Download size={15} />} onClick={exportReport}>Export (JSON)</Button>
        </div>
      </div>

      {/* Inventory */}
      <Section icon={<FileText size={16} />} title="Inventory"
        hint="Every represented agent in scope and its ownership-lifecycle state.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <MetricCard label="Represented agents" value={inv.represented ?? 0} />
          <MetricCard label="Materiality scored" value={mat.scored ?? 0} />
          <MetricCard label="Unscored" value={mat.unscored ?? 0} tone={(mat.unscored ?? 0) > 0 ? 'warning' : 'muted'} />
          <MetricCard label="Open exceptions" value={exc.total ?? 0} tone={(exc.total ?? 0) > 0 ? 'danger' : 'success'} />
        </div>
        <Card padding="none">
          {stateRows.length === 0 ? (
            <div className="p-3 text-[12.5px] text-v2-ink-muted">No represented agents in scope yet.</div>
          ) : (
            <Table
              columns={[
                { key: 'state', header: 'Lifecycle state', render: (r) => <span className="text-v2-ink">{r.state}</span> },
                { key: 'count', header: 'Agents', align: 'right', render: (r) => <span className="text-v2-ink tabular-nums">{r.count}</span> },
              ]}
              rows={stateRows}
              rowKey={(r) => r.state}
              ariaLabel="Inventory by lifecycle state"
            />
          )}
        </Card>
      </Section>

      {/* Materiality method */}
      <Section icon={<Scale size={16} />} title="Materiality method"
        hint={`Tiers determined by the platform materiality rubric ${mat.rubricVersion ? `v${mat.rubricVersion}` : ''}.`}>
        <div className="flex flex-wrap gap-2">
          {['TIER_1', 'TIER_2', 'TIER_3', 'TIER_4'].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <TierBadge tier={t} />
              <span className="text-[12px] text-v2-ink-secondary tabular-nums">{(mat.byTier && mat.byTier[t]) || 0}</span>
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <TierBadge tier={null} label="unscored" />
            <span className="text-[12px] text-v2-ink-secondary tabular-nums">{mat.unscored ?? 0}</span>
          </span>
        </div>
      </Section>

      {/* Independence */}
      <Section icon={<UserCheck size={16} />} title="Independence (segregation of duties)"
        hint="Is the accountable owner a different human than the creator of the system?">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MetricCard label="Independent (owner ≠ creator)" value={ind.segregated ?? 0} tone="success" />
          <MetricCard label="Self-owned (owner = creator)" value={ind.selfOwned ?? 0} tone={(ind.selfOwned ?? 0) > 0 ? 'warning' : 'muted'} />
          <MetricCard label="No accepted owner" value={ind.ownerless ?? 0} tone={(ind.ownerless ?? 0) > 0 ? 'danger' : 'muted'} />
        </div>
      </Section>

      {/* Attestation posture */}
      <Section icon={<ShieldCheck size={16} />} title="Attestation posture"
        hint="Owner re-attestation status across the represented estate.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Current" value={att.current ?? 0} tone="success" />
          <MetricCard label="Due soon" value={att.stale ?? 0} tone={(att.stale ?? 0) > 0 ? 'warning' : 'muted'} />
          <MetricCard label="Overdue" value={att.lapsed ?? 0} tone={(att.lapsed ?? 0) > 0 ? 'danger' : 'muted'} />
          <MetricCard label="Never attested" value={att.none ?? 0} tone={(att.none ?? 0) > 0 ? 'warning' : 'muted'} />
        </div>
      </Section>

      {/* Exceptions */}
      <Section icon={<AlertTriangle size={16} />} title={`Exceptions (${exc.total ?? 0})`}
        hint="Open ownership gaps with the remediation each requires — the same findings remediated in Outcomes & Actions.">
        <Card padding="none">
          {findings.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck size={22} />}
              title="No open exceptions"
              description="Every represented agent has an accountable owner and current attestation."
            />
          ) : (
            <Table columns={exceptionColumns} rows={findings}
              rowKey={(f, i) => `${f.type}:${f.agentId || 'agg'}:${i}`} ariaLabel="Open exceptions" />
          )}
        </Card>
      </Section>

      <Callout tone="trust" className="mt-4">
        <b className="text-v2-ink">Honest boundary.</b> This report answers &ldquo;no system in production without a named owner, and every gap surfaced&rdquo; — not &ldquo;we found every system.&rdquo; Coverage grows with ingest connectors; the exception register is the proof of honesty.
      </Callout>
    </div>
  );
}
